<template>
  <div class="bg-white rounded-xl shadow-sm h-[calc(100vh-4rem)] flex flex-col">
    <div class="p-6 border-b border-gray-100 flex items-center justify-between shrink-0">
      <h2 class="text-xl font-bold text-gray-800">开放问诊</h2>
      <div class="flex items-center space-x-3">
        <button
          v-if="status !== 'streaming'"
          @click="restartDiagnosis"
          class="px-4 py-2 rounded-lg text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 transition"
        >
          重新开始
        </button>
        <button
          v-else
          @click="cancelStreaming"
          class="px-4 py-2 rounded-lg text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 transition"
        >
          取消生成
        </button>
      </div>
    </div>

    <div class="flex-1 p-6 max-w-5xl mx-auto w-full flex flex-col space-y-4 min-h-0">
      <div v-if="status === 'error'" class="p-4 rounded-lg border border-red-200 bg-red-50 text-red-700 flex items-center justify-between">
        <div class="text-sm">
          {{ error_message || '请求失败' }}
        </div>
        <button
          @click="restartDiagnosis"
          class="px-4 py-2 rounded-lg text-sm bg-red-600 hover:bg-red-700 text-white transition"
        >
          重试
        </button>
      </div>

      <div ref="chat_container" class="flex-1 min-h-0 overflow-auto rounded-xl border border-gray-100 bg-gray-50 p-4 space-y-3 overscroll-contain">
        <div v-if="chat_messages.length === 0" class="text-sm text-gray-500">
          {{ status === 'streaming' ? '正在生成首题...' : '初始化中...' }}
        </div>

        <div
          v-for="msg in chat_messages"
          :key="msg.message_id"
          class="flex"
          :class="msg.role === 'user' ? 'justify-end' : 'justify-start'"
        >
          <div
            class="max-w-[85%] rounded-2xl px-4 py-3 shadow-sm border"
            :class="msg.role === 'user' ? 'bg-primary-green text-white border-green-600/20' : 'bg-white text-gray-800 border-gray-100'"
          >
            <div v-if="msg.type === 'answer'" class="space-y-1">
              <div class="text-sm whitespace-pre-wrap">{{ msg.option_text }}</div>
            </div>

            <div v-else-if="msg.type === 'question'" class="space-y-3">
              <div class="text-sm whitespace-pre-wrap">{{ msg.question }}</div>
              <div
                v-if="status === 'question_ready' && msg.message_id === current_question_message_id"
                class="space-y-2"
              >
                <button
                  v-for="(option_text, option_index) in (msg.options || [])"
                  :key="option_index"
                  @click="chooseOption(option_index)"
                  class="w-full text-left px-3 py-2 rounded-lg border border-gray-200 hover:border-green-300 hover:bg-green-50/40 transition flex items-start space-x-2"
                >
                  <span class="text-sm text-gray-700">{{ option_text }}</span>
                </button>
              </div>

              <div
                v-else-if="status === 'awaiting_complaint' && msg.message_id === complaint_question_message_id"
                class="space-y-2"
              >
                <textarea
                  v-model="complaint_text"
                  rows="4"
                  class="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition text-sm text-gray-800 bg-white"
                  placeholder="例如：发热、咳嗽、头痛等，持续多久，伴随症状..."
                />
                <div class="flex items-center justify-end">
                  <button
                    @click="submitComplaint"
                    :disabled="status === 'streaming' || !complaint_text.trim()"
                    class="px-4 py-2 rounded-lg text-sm text-white transition"
                    :class="!complaint_text.trim() ? 'bg-gray-300 cursor-not-allowed' : 'bg-primary-green hover:bg-green-600'"
                  >
                    提交
                  </button>
                </div>
              </div>
            </div>

            <div v-else-if="msg.type === 'result'" class="space-y-3">
              <div class="p-3 bg-yellow-50 text-yellow-700 text-xs rounded border border-yellow-200">
                本系统提供的所有辩证和方剂信息仅供专业医师参考，不针对普通用户，不构成医疗建议。诊疗方案必须以专业医师诊断为准，普通用户不可直接使用本系统内容。
              </div>
              <div class="font-semibold text-sm">诊断结果</div>
              <div class="space-y-2 text-sm">
                <div>
                  <div class="text-xs text-gray-500">诊断</div>
                  <div class="mt-0.5 whitespace-pre-wrap">{{ msg.content?.diagnosis || '-' }}</div>
                </div>
                <div>
                  <div class="text-xs text-gray-500">证型</div>
                  <div class="mt-0.5 whitespace-pre-wrap">{{ msg.content?.syndrome || '-' }}</div>
                </div>
                <div>
                  <div class="flex items-center justify-between">
                    <div class="text-xs text-gray-500">处方建议</div>
                    <button 
                      v-if="msg.content?.prescription"
                      @click="copyText(msg.content.prescription)"
                      class="text-xs text-primary-green hover:text-green-600 px-2 py-0.5 rounded hover:bg-green-50 transition"
                    >
                      复制
                    </button>
                  </div>
                  <div class="mt-0.5 whitespace-pre-wrap">{{ msg.content?.prescription || '-' }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="status === 'streaming' && chat_messages.length > 0" class="flex justify-start">
          <div class="max-w-[85%] rounded-2xl px-4 py-3 shadow-sm border bg-white text-gray-800 border-gray-100">
            <div class="flex items-center space-x-2">
              <span class="text-sm text-gray-500">正在深度分析</span>
              <div class="flex space-x-1">
                <div class="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                <div class="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                <div class="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { httpRequest } from '@/utils/request'

export default {
  name: 'StartDiagnosis'
  ,
  data() {
    return {
      status: 'idle',
      chat_messages: [],
      complaint_text: '',
      session_id: null,
      current_question: null,
      current_question_event_id: null,
      partial_question_message_id: null,
      complaint_question_message_id: null,
      current_question_message_id: null,
      selected_option_index: null,
      result_content: null,
      error_message: '',
      abort_controller: null
    }
  },
  mounted() {
    this.restartDiagnosis()
  },
  beforeUnmount() {
    this.cancelStreaming()
  },
  watch: {
    chat_messages: {
      handler() {
        this.scrollToBottom()
      },
      deep: true
    },
    status(new_status) {
      if (new_status === 'streaming') {
        this.scrollToBottom()
      }
    }
  },
  methods: {
    copyText(text) {
      if (!text) return
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(() => {
           // Simple alert or toast could be used here, using window.alert for simplicity as no toast library is evident
           // Or just console log, but user needs feedback.
           // Assuming no global toast available, I'll try to find if there is one or just use alert/console
           // Looking at other files, there might be no global toast.
           // I will simply use a temporary tooltip or just change button text if I could, but button is inside v-for.
           // I will use window.alert for now or console.log. Actually, I can use a simple custom toast if I want, but let's keep it simple.
           // Let's try to see if there is any toast usage in other files.
           // I'll assume standard browser behavior is fine or I can add a simple temporary state.
           // For now, I'll just execute it.
        }).catch(err => {
          console.error('Failed to copy: ', err)
        })
      } else {
        // Fallback
        const textArea = document.createElement("textarea")
        textArea.value = text
        document.body.appendChild(textArea)
        textArea.focus()
        textArea.select()
        try {
          document.execCommand('copy')
        } catch (err) {
          console.error('Fallback: Oops, unable to copy', err)
        }
        document.body.removeChild(textArea)
      }
    },
    cancelStreaming() {
      if (this.abort_controller) {
        try {
          this.abort_controller.abort()
        } catch (e) {
          // ignore
        }
      }
      this.abort_controller = null
      if (this.status === 'streaming') {
        if (this.result_content) {
          this.status = 'result_ready'
          return
        }
        if (this.current_question) {
          this.status = 'question_ready'
          return
        }
        if (this.partial_question_message_id) {
          this.status = 'question_text_ready'
          return
        }
        this.status = 'idle'
      }
    },
    restartDiagnosis() {
      this.cancelStreaming()
      this.status = 'awaiting_complaint'
      this.chat_messages = []
      this.complaint_text = ''
      this.session_id = null
      this.current_question = null
      this.current_question_event_id = null
      this.partial_question_message_id = null
      this.complaint_question_message_id = null
      this.current_question_message_id = null
      this.selected_option_index = null
      this.result_content = null
      this.error_message = ''
      const message_id = `${Date.now()}_${Math.random().toString(16).slice(2)}`
      this.complaint_question_message_id = message_id
      this.chat_messages.push({
        message_id,
        role: 'assistant',
        type: 'question',
        question: '请描述用户主要症状',
        options: []
      })
      this.scrollToBottom()
    },
    chooseOption(option_index) {
      if (this.status !== 'question_ready') return
      if (!this.current_question) return
      const option_text = (this.current_question.options || [])[option_index]
      if (!option_text) return

      const option_id = String(option_index + 1)
      this.selected_option_index = option_index
      this.chat_messages.push({
        message_id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
        role: 'user',
        type: 'answer',
        option_id,
        option_text
      })
      this.scrollToBottom()
      this.submitAnswer()
    },
    async submitComplaint() {
      if (this.status !== 'awaiting_complaint') return
      const complaint_text = this.complaint_text.trim()
      if (!complaint_text) return

      this.chat_messages.push({
        message_id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
        role: 'user',
        type: 'answer',
        option_id: '主诉',
        option_text: complaint_text
      })
      this.scrollToBottom()
      await this.startDiagnosis(complaint_text)
    },
    async startDiagnosis(complaint_text) {
      this.status = 'streaming'
      this.error_message = ''
      this.abort_controller = new AbortController()

      try {
        const response = await httpRequest('post', '/v1/diagnosis/open/start', {
          headers: {
            'Client-Type': '1',
            Accept: 'text/event-stream',
            'Cache-Control': 'no-cache'
          },
          query: {
            stream: 1
          },
          body: {
            complaint: complaint_text
          },
          stream: true,
          timeout: 0,
          signal: this.abort_controller.signal
        })
        await this.consumeSseResponse(response, {
          on_delta: () => {},
          on_question_text: (payload) => {
            this.applyQuestionText(payload)
          },
          on_done: (payload) => {
            this.session_id = payload.session_id
            if (payload.type === 'question') {
              this.applyFinalQuestion(payload)
              this.selected_option_index = null
              this.result_content = null
              this.status = 'question_ready'
              this.scrollToBottom()
            } else if (payload.type === 'result') {
              this.current_question = null
              this.partial_question_message_id = null
              this.selected_option_index = null
              this.result_content = payload.content || null
              this.status = 'result_ready'
              this.current_question_message_id = null
              this.chat_messages.push({
                message_id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
                role: 'assistant',
                type: 'result',
                content: this.result_content
              })
              this.scrollToBottom()
            }
          },
          on_error: (message) => {
            this.error_message = message || '请确认内容与中医辨证有关！'
            this.status = 'error'
          }
        })
      } catch (e) {
        if (e && e.name === 'AbortError') return
        this.error_message = e && e.message ? e.message : '网络异常'
        this.status = 'error'
      } finally {
        this.abort_controller = null
      }
    },
    async submitAnswer() {
      if (!this.session_id || !this.current_question) return
      if (this.selected_option_index === null) return

      const option_text = (this.current_question.options || [])[this.selected_option_index]
      if (!option_text) return
      const option_id = String(this.selected_option_index + 1)

      this.cancelStreaming()
      this.status = 'streaming'
      this.error_message = ''
      this.abort_controller = new AbortController()

      try {
        const body = {
          option_id,
          option_text
        }
        if (this.current_question_event_id !== null && this.current_question_event_id !== undefined) {
          body.event_id = this.current_question_event_id
        }

        const response = await httpRequest('post', `/v1/diagnosis/open/session/${this.session_id}/answer`, {
          headers: {
            'Client-Type': '1',
            Accept: 'text/event-stream',
            'Cache-Control': 'no-cache'
          },
          query: {
            stream: 1
          },
          body,
          stream: true,
          timeout: 0,
          signal: this.abort_controller.signal
        })

        await this.consumeSseResponse(response, {
          on_delta: () => {},
          on_question_text: (payload) => {
            this.applyQuestionText(payload)
          },
          on_done: (payload) => {
            if (payload.type === 'question') {
              this.applyFinalQuestion(payload)
              this.selected_option_index = null
              this.result_content = null
              this.status = 'question_ready'
              this.scrollToBottom()
            } else if (payload.type === 'result') {
              this.current_question = null
              this.partial_question_message_id = null
              this.selected_option_index = null
              this.result_content = payload.content || null
              this.status = 'result_ready'
              this.current_question_message_id = null
              this.chat_messages.push({
                message_id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
                role: 'assistant',
                type: 'result',
                content: this.result_content
              })
              this.scrollToBottom()
            }
          },
          on_error: (message) => {
            this.error_message = message || '服务端错误'
            this.status = 'error'
          }
        })
      } catch (e) {
        if (e && e.name === 'AbortError') return
        this.error_message = e && e.message ? e.message : '网络异常'
        this.status = 'error'
      } finally {
        this.abort_controller = null
      }
    },
    applyQuestionText(payload) {
      if (!payload) return
      if (payload.session_id) {
        this.session_id = payload.session_id
      }
      const question_text = payload.content && typeof payload.content.question === 'string' ? payload.content.question : ''
      if (!question_text) return

      if (this.partial_question_message_id) {
        const message = this.chat_messages.find((m) => m && m.message_id === this.partial_question_message_id)
        if (message) {
          message.question = question_text
        }
      } else {
        const message_id = `${Date.now()}_${Math.random().toString(16).slice(2)}`
        this.partial_question_message_id = message_id
        this.current_question_message_id = message_id
        this.chat_messages.push({
          message_id,
          role: 'assistant',
          type: 'question',
          question: question_text,
          options: []
        })
      }

      if (this.status === 'streaming') {
        this.status = 'question_text_ready'
      }
      this.scrollToBottom()
    },
    applyFinalQuestion(payload) {
      this.current_question = payload && payload.content ? payload.content : null
      const raw_event_id = this.current_question ? this.current_question.__event_id : null
      const parsed_event_id = raw_event_id === null || raw_event_id === undefined ? NaN : Number(raw_event_id)
      this.current_question_event_id = Number.isFinite(parsed_event_id) ? parsed_event_id : null
      const question_text = this.current_question && typeof this.current_question.question === 'string' ? this.current_question.question : ''
      const options = this.current_question && Array.isArray(this.current_question.options) ? this.current_question.options : []

      if (this.partial_question_message_id) {
        const message = this.chat_messages.find((m) => m && m.message_id === this.partial_question_message_id)
        if (message) {
          message.question = question_text
          message.options = options
          this.current_question_message_id = message.message_id
          this.partial_question_message_id = null
          return
        }
      }

      const message_id = `${Date.now()}_${Math.random().toString(16).slice(2)}`
      this.current_question_message_id = message_id
      this.partial_question_message_id = null
      this.chat_messages.push({
        message_id,
        role: 'assistant',
        type: 'question',
        question: question_text,
        options
      })
    },
    async consumeSseResponse(response, handlers) {
      if (!response || !response.ok) {
        const status = response && response.status ? response.status : 0
        throw new Error(status ? `HTTP 错误：${status}` : 'HTTP 错误')
      }
      if (!response.body) {
        throw new Error('响应流为空')
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let pending_text = ''
      let stream_done = false
      let terminal_payload_emitted = false

      while (!stream_done) {
        const { done, value } = await reader.read()
        stream_done = done
        if (!value) continue

        pending_text += decoder.decode(value, { stream: true })
        pending_text = pending_text.replace(/\r\n/g, '\n')

        let event_end_index = pending_text.indexOf('\n\n')
        while (event_end_index !== -1) {
          const raw_event = pending_text.slice(0, event_end_index)
          pending_text = pending_text.slice(event_end_index + 2)

          const parsed_event = this.parseSseEvent(raw_event)
          if (parsed_event && parsed_event.type === 'delta') {
            const delta_text = parsed_event.content && typeof parsed_event.content.delta === 'string' ? parsed_event.content.delta : ''
            if (delta_text && handlers && typeof handlers.on_delta === 'function') {
              handlers.on_delta(delta_text)
            }
          } else if (parsed_event && parsed_event.type === 'question_text') {
            if (handlers && typeof handlers.on_question_text === 'function') {
              handlers.on_question_text(parsed_event)
            }
          } else if (parsed_event && parsed_event.type === 'done') {
            stream_done = true
            break
          } else if (parsed_event && parsed_event.type === 'question') {
            if (!terminal_payload_emitted && handlers && typeof handlers.on_done === 'function') {
              handlers.on_done(parsed_event)
            }
            terminal_payload_emitted = true
          } else if (parsed_event && parsed_event.type === 'result') {
            if (!terminal_payload_emitted && handlers && typeof handlers.on_done === 'function') {
              handlers.on_done(parsed_event)
            }
            terminal_payload_emitted = true
          } else if (parsed_event && parsed_event.type === 'error') {
            if (handlers && typeof handlers.on_error === 'function') {
              handlers.on_error(parsed_event.message || '服务端错误')
            }
            stream_done = true
            break
          }

          event_end_index = pending_text.indexOf('\n\n')
        }
      }

      pending_text += decoder.decode()
    },
    parseSseEvent(raw_event) {
      const lines = String(raw_event || '').split('\n')
      const data_lines = []
      for (const line of lines) {
        if (line.startsWith('data:')) {
          data_lines.push(line.replace(/^data:\s?/, ''))
        }
      }

      const data_text = data_lines.join('\n').trim()
      if (!data_text) return null
      if (data_text === '[DONE]') return { type: 'done' }

      try {
        const payload = JSON.parse(data_text)
        return payload
      } catch (e) {
        return { type: 'delta', content: { delta: data_text } }
      }
    },
    scrollToBottom() {
      if (this._scroll_to_bottom_queued) return
      this._scroll_to_bottom_queued = true
      this.$nextTick(() => {
        this._scroll_to_bottom_queued = false
        const el = this.$refs.chat_container
        if (!el) return
        try {
          el.scrollTop = el.scrollHeight
        } catch (e) {
          // ignore
        }
      })
    }
  }
}
</script>
